import { forwardRef } from "react";
import FirmProfile from "../../../config/firmProfile";

const styles = {
    page: { 
        padding: '0', 
        fontFamily: 'Times New Roman, serif',
        color: '#000',
        backgroundColor: '#fff',
        position: 'relative',
        minHeight: '100%',
    },
    headerContainer: {
        width: '100%',
        height: '40px',
        position: 'relative',
        marginBottom: '40px',
    },
    contentContainer: {
        padding: '0 40px',
    },
    logo: { height: '60px', marginBottom: '10px' },
    title: { 
        textAlign: 'center', 
        fontSize: '24px', 
        fontWeight: 'bold', 
        marginBottom: '20px',
        textTransform: 'uppercase'
    },
    section: { marginBottom: '16px', pageBreakInside: 'avoid' },
    sectionTitle: { 
        fontSize: '16px', 
        fontWeight: 'bold', 
        borderBottom: '1px solid #ccc', 
        paddingBottom: '5px', 
        marginBottom: '8px',
        pageBreakAfter: 'avoid',
        breakAfter: 'avoid',
    },
    text: { fontSize: '12px', lineHeight: 1.5, marginBottom: '6px' },
    bottomText: { fontSize: '12px', lineHeight: 1.5, marginBottom: '8px', color: '#1a237e' },
    table: { width: '100%', borderCollapse: 'collapse', marginBottom: '10px', pageBreakInside: 'avoid' },
    th: { borderBottom: '1px solid #000', textAlign: 'left', padding: '5px', fontSize: '12px', fontWeight: 'bold' },
    td: { borderBottom: '1px solid #eee', textAlign: 'left', padding: '5px', fontSize: '12px' },
    totalRow: { borderTop: '2px solid #000', fontWeight: 'bold' },
    signatureBlock: { marginTop: '30px', display: 'flex', justifyContent: 'space-between', pageBreakInside: 'avoid', breakInside: 'avoid' },
    signatureBox: { width: '45%' },
    sigText: { fontFamily: 'cursive', fontSize: '14px', color: '#1a237e', marginTop: '10px' },
    headerLogoContainer: {
        display: 'flex', 
        justifyContent: 'center', 
        marginBottom: '20px'
    }
};

const PdfTemplate = forwardRef(({ quotation }, ref) => {
  if (!quotation) return null;

  const { client, businessStructure, services, totals, firmSignOff, acknowledgement, id, createdAt } = quotation;

  const getAllServices = () => {
      const all = [];
      ['bookkeeping', 'taxation', 'structuring'].forEach(key => {
          if (services?.[key]?.selected) {
              services[key].selected.forEach(s => {
                  let label = s.label;
                  if (s.duration) {
                      label += ` (${s.duration})`;
                  }
                  all.push({ category: key, label, price: s.price });
              });
          }
           if (services?.[key]?.others) {
              services[key].others.forEach(s => {
                   if (s.text) {
                        let label = `Other: ${s.text}`;
                        if (s.duration) {
                            label += ` (${s.duration})`;
                        }
                        all.push({ category: key, label, price: s.price });
                   }
              });
          }
          if (services?.[key]?.other?.text) {
               all.push({ category: key, label: `Other: ${services[key].other.text}`, price: services[key].other.price });
          }
      });
      return all;
  };

  const serviceItems = getAllServices();

  return (
    <div ref={ref} style={styles.page}>
        {/* Geometric Header */}
        {/* Geometric Header SVG */}
        <div style={styles.headerContainer}>
            <svg width="100%" height="40" viewBox="0 0 1000 40" preserveAspectRatio="none" style={{ display: 'block' }}>
                {/* Row 1 */}
                <polygon points="0,0 150,0 175,20 0,20" fill="#7ec942" />
                <polygon points="125,0 600,0 625,20 150,20" fill="#1a237e" />
                {/* Row 2 (offset) */}
                <polygon points="625,20 655,20 675,40 645,40" fill="#7ec942" />
                <polygon points="665,20 1000,20 1000,40 685,40" fill="#7ec942" />
            </svg>
        </div>

        {/* Padding Container for Inner Content */}
        <div style={styles.contentContainer}>
            {/* Logo */}
            <div style={styles.headerLogoContainer}>
                {FirmProfile.LOGO_URL && <img src={FirmProfile.LOGO_URL} alt="Logo" style={styles.logo} />}
            </div>

            {/* Contact Info */}
            <div style={{...styles.text, textAlign: 'center', marginBottom: '20px', fontSize: '10px', color: '#666'}}>
                <div>{FirmProfile.FOOTER_ABN_LINE}</div>
                <div>
                    Ph: {FirmProfile.CONTACT_DETAILS.phone} | Email: {FirmProfile.CONTACT_DETAILS.email} | {FirmProfile.CONTACT_DETAILS.website}
                </div>
                <div>{FirmProfile.CONTACT_DETAILS.address}</div>
            </div>

            {/* Title */}
            <div style={styles.title}>{businessStructure?.pdfTitle || "Business Engagement / Quotation"}</div>

            {/* Meta */}
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
                <span style={styles.text}><strong>Reference:</strong> {id}</span>
                <span style={styles.text}><strong>Date:</strong> {new Date(createdAt || Date.now()).toLocaleDateString()}</span>
            </div>

            {/* Client Details */}
            <div style={styles.section}>
                {/* <div style={styles.sectionTitle}>Client Details</div> */}
                <div style={styles.text}>
                    <strong>Attention:</strong> {client?.clientName}<br/>
                    {client?.businessName && <><strong>Business Name:</strong> {client.businessName}<br/></>}
                    <strong>Email:</strong> {client?.clientEmail}<br/>
                    {client?.clientAddress && <><strong>Address:</strong> {client.clientAddress}<br/></>}
                </div>
            </div>

            {/* Opening Paragraph */}
            <div style={styles.section}>
                 <p style={styles.text}>Given the recent discussion, we are pleased to provide this quotation for accounting services designed to support your business needs.</p>
            </div>

            {/* Business Structure */}
            <div style={styles.section}>
                <div style={styles.sectionTitle}>Business Structure</div>
                <p style={styles.text}>{businessStructure?.structure} {businessStructure?.businessStructureOther && `(${businessStructure.businessStructureOther})`}</p>
            </div>

            {/* Services Table */}
            <div style={styles.section}>
                <div style={styles.sectionTitle}>Services & Pricing</div>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Service Category</th>
                            <th style={styles.th}>Description</th>
                            <th style={{...styles.th, textAlign: 'right'}}>Amount (AUD)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {serviceItems.map((item, idx) => (
                            <tr key={`${item.category}-${idx}`}>
                                <td style={styles.td}>{item.category.charAt(0).toUpperCase() + item.category.slice(1)}</td>
                                <td style={styles.td}>{item.label}</td>
                                 <td style={{...styles.td, textAlign: 'right'}}>${Number(item.price || 0).toFixed(2)}</td>
                            </tr>
                        ))}
                        <tr style={styles.totalRow}>
                            <td colSpan={2} style={{...styles.td, textAlign: 'right'}}>Subtotal (Excl. GST)</td>
                            <td style={{...styles.td, textAlign: 'right'}}>${totals?.subtotal?.toFixed(2)}</td>
                        </tr>
                        <tr>
                            <td colSpan={2} style={{...styles.td, textAlign: 'right'}}>GST (10%)</td>
                            <td style={{...styles.td, textAlign: 'right'}}>${totals?.gst?.toFixed(2)}</td>
                        </tr>
                        <tr style={{...styles.totalRow, backgroundColor: '#f9f9f9'}}>
                            <td colSpan={2} style={{...styles.td, textAlign: 'right', fontWeight: 'bold'}}>Total (Incl. GST)</td>
                            <td style={{...styles.td, textAlign: 'right', fontWeight: 'bold'}}>${totals?.grandTotal?.toFixed(2)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Closing */}
            <div style={styles.section}>
                <p style={styles.text}>We look forward to the opportunity of working with you and supporting your ongoing accounting and compliance requirements, Should you have any questions regarding this quotation or require further information, please feel free to contact us.</p>
                
                {(businessStructure?.hasClosingStatement === true || businessStructure?.hasClosingStatement === 'true' || businessStructure?.hasClosingStatement === 'yes') && businessStructure?.closingStatementText && (
                    <div style={{ marginTop: '15px' }}>
                        {businessStructure.closingStatementText.split('\n').map((line, idx) => (
                            <p key={idx} style={{ ...styles.text, fontStyle: 'italic', margin: '0 0 4px 0', minHeight: '1.2em' }}>{line}</p>
                        ))}
                    </div>
                )}
            </div>

            {/* Signatures */}
            <div style={styles.signatureBlock}>
                {/* Firm */}
                <div style={styles.signatureBox}>
                    <div style={styles.sectionTitle}>Firm Sign-off</div>
                    <div style={styles.text}>Kind Regards,</div>
                    <div style={styles.sigText}>
                        Digitally signed by {FirmProfile.PRINCIPAL_SIGNER.name} on {firmSignOff?.signedDate}
                    </div>
                    <div style={styles.bottomText}>
                        <strong>{FirmProfile.PRINCIPAL_SIGNER.name}</strong><br/>
                        {FirmProfile.PRINCIPAL_SIGNER.title}<br/>
                        {FirmProfile.COMPANY_NAME}
                    </div>
                </div>

                {/* Client */}
                <div style={styles.signatureBox}>
                    <div style={styles.sectionTitle}>Client Acknowledgement</div>
                    {acknowledgement?.directorName && (
                        <>
                             <div style={styles.text}>
                                On Behalf Of: {acknowledgement.onBehalfOf}
                             </div>
                             <div style={styles.sigText}>
                                Digitally signed by {acknowledgement.directorName} on {acknowledgement.clientSignedDate}
                             </div>
                             <div style={styles.text}>
                                 <strong>{acknowledgement.directorName}</strong>
                             </div>
                        </>
                    )}
                </div>
            </div>

            {/* Geometric Footer - in normal document flow */}
            <div style={{ marginTop: '20px' }}>
                <svg width="100%" height="15" viewBox="0 0 1000 15" preserveAspectRatio="none" style={{ display: 'block' }}>
                    <polygon points="0,0 300,0 330,15 0,15" fill="#7ec942" />
                    <polygon points="330,0 380,0 430,15 380,15" fill="#7ec942" />
                    <polygon points="430,0 730,0 760,15 460,15" fill="#1a237e" />
                    <polygon points="760,0 810,0 860,15 810,15" fill="#7ec942" />
                    <polygon points="860,0 1000,0 1000,15 910,15" fill="#7ec942" />
                </svg>
            </div>
        </div>
    </div>
  );
});

PdfTemplate.displayName = "PdfTemplate";

export default PdfTemplate;
